import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles } from "lucide-react";

const HeroSection = () => {
  const containerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToShop = (e) => {
    e.preventDefault();
    const shopSection = document.getElementById('shop');
    if (shopSection) {
      shopSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={containerRef}
      id="home"
      className="relative h-screen min-h-[700px] overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video Background with Scale Effect */}
      <motion.div style={{ scale }} className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="video-bg"
          poster="https://images.unsplash.com/photo-1773084258196-f8799453be7e?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
          data-testid="hero-video"
        >
          <source
            src="https://customer-assets.emergentagent.com/job_scalp-nourish-proto/artifacts/w1dvgf8w_4820797-hd_1920_1080_25fps.mp4"
            type="video/mp4"
          />
        </video>
        {/* Enhanced Overlay with Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-deepGreen/60 via-brand-deepGreen/40 to-brand-deepGreen/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />
      </motion.div>

      {/* Floating Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ 
            y: [0, -20, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 w-32 h-32 bg-brand-warmGold/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            y: [0, 20, 0],
            opacity: [0.2, 0.5, 0.2]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute top-40 right-20 w-48 h-48 bg-brand-warmGold/15 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            y: [0, -15, 0],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/10 rounded-full blur-3xl"
        />
      </div>

      {/* Content */}
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 pt-20 md:pt-16"
      >
        {/* Premium Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex items-center gap-3 mb-6"
        >
          <div className="h-px w-12 bg-gradient-to-r from-transparent to-brand-warmGold" />
          <div className="flex items-center gap-2 px-5 py-2 rounded-full border border-brand-warmGold/40 bg-brand-warmGold/10 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-brand-warmGold" strokeWidth={1.5} />
            <span className="text-brand-warmGold text-xs tracking-[0.25em] uppercase font-body font-medium">
              Premium Herbal Hair Care
            </span>
          </div>
          <div className="h-px w-12 bg-gradient-to-l from-transparent to-brand-warmGold" />
        </motion.div>

        {/* Main Headline with Staggered Animation */}
        <div className="overflow-hidden mb-3">
          <motion.h1
            initial={{ y: 100, opacity: 0 }}
            animate={isLoaded ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 1, delay: 0.5, ease: [0.25, 0.1, 0, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light text-white leading-[0.95] tracking-tight"
          >
            Grow with
          </motion.h1>
        </div>
        <div className="overflow-hidden mb-2">
          <motion.h1
            initial={{ y: 100, opacity: 0 }}
            animate={isLoaded ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 1, delay: 0.65, ease: [0.25, 0.1, 0, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light text-white leading-[0.95] tracking-tight"
          >
            Intention.
          </motion.h1>
        </div>
        <div className="overflow-hidden mb-6">
          <motion.span
            initial={{ y: 100, opacity: 0 }}
            animate={isLoaded ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 1, delay: 0.8, ease: [0.25, 0.1, 0, 1] }}
            className="block font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light italic"
            style={{
              background: "linear-gradient(135deg, #C5A059 0%, #E5D4A8 50%, #C5A059 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Glow Naturally.
          </motion.span>
        </div>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
          className="text-white/80 text-base sm:text-lg font-body font-light max-w-xl mb-8 leading-relaxed"
        >
          Premium herbal hair oils and nourishing hair grease crafted to support
          stronger, healthier-looking hair with a tropical touch.
        </motion.p>

        {/* Single CTA Button - Scrolls to Shop Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isLoaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <button
            onClick={scrollToShop}
            className="group relative inline-flex items-center gap-3 px-12 py-5 bg-brand-warmGold text-brand-deepGreen rounded-full font-body font-semibold text-sm tracking-wider uppercase overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-brand-warmGold/40 hover:-translate-y-1"
            data-testid="hero-cta-shop"
          >
            <span className="relative z-10">Shop Now</span>
            <div className="absolute inset-0 bg-gradient-to-r from-brand-warmGold via-[#E5D4A8] to-brand-warmGold opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </button>
        </motion.div>
      </motion.div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-brand-cream to-transparent z-10 pointer-events-none" />
    </section>
  );
};

export default HeroSection;
